/* eslint-disable @typescript-eslint/no-explicit-any */
import TextareaBase from 'core/modules/atoms/Textarea';
import EducationYearSelectorBase from 'core/modules/components/selects/EducationYearSelector';
import EvaluationCriteriaSelectorBase from 'core/modules/components/selects/EvaluationCriteriaSelector/EvaluationCriteriaSelector';
import SpecialNeedsSelectorBase from 'core/modules/components/selects/SpecialNeedsSelector';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, InputText } from 'ui';
import type { EvaluationCriteriaSelectedItem } from 'app/types/evaluationCriteria';
import TextInputOrLessonSelectorBase from '../TextInputOrLessonSelector';
import './_ai-quiz-section.scss';
import ActivityTypeSelector from './components/ActivityTypeSelector';

// Cast JavaScript components to any to avoid TypeScript errors
const Textarea = TextareaBase as any;
const EducationYearSelector = EducationYearSelectorBase as any;
const EvaluationCriteriaSelector = EvaluationCriteriaSelectorBase as any;
const SpecialNeedsSelector = SpecialNeedsSelectorBase as any;
const TextInputOrLessonSelector = TextInputOrLessonSelectorBase as any;

type TopicLesson = {
  guid: string;
  name: string;
  content: string;
};

type ContentSource = {
  id: string;
  type: 'lesson' | 'module';
  title: string;
  unitId?: string;
  unitTitle?: string;
  courseGuid?: string;
  courseName?: string;
};

type AIQuizTopic = {
  mode: 'text' | 'lesson';
  text: string;
  lesson: TopicLesson | null;
  lessons: TopicLesson[];
  lessonIds?: string[];
  contentIdsMap?: Record<string, string[]>;
  sources?: ContentSource[];
};

type DifficultyLevel = {
  educationLevel: string;
  educationYear: string;
};

type EvaluationCriteria = {
  selected: Array<EvaluationCriteriaSelectedItem | string>;
  additional: string;
};

export type AIQuizMetadata = {
  topic: AIQuizTopic;
  difficultyLevel: DifficultyLevel;
  numberOfQuestions: number;
  activityTypeGuids: string[];
  evaluationCriteria: EvaluationCriteria;
  specialNeeds: string[];
  additionalNotes: string;
  lessonIdAssignments?: Array<{ questionId: string; lessonId: string }>;
};

export type AIQuizSectionProps = {
  value?: AIQuizMetadata;
  onChange: (metadata: AIQuizMetadata) => void;
  disabled?: boolean;
  onChangeTitle?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  titleTest?: string;
  withLabel?: boolean;
  defaultCourseId?: string;
  disciplineId?: string;
};

const DEFAULT_VALUE: AIQuizMetadata = {
  topic: { mode: 'text', text: '', lesson: null, lessons: [], lessonIds: [], contentIdsMap: {} },
  difficultyLevel: { educationLevel: '', educationYear: '' },
  numberOfQuestions: 4,
  activityTypeGuids: [],
  evaluationCriteria: { selected: [], additional: '' },
  specialNeeds: [],
  additionalNotes: '',
};

const MAX_QUESTIONS = 10;

function AIQuizSection({
  value = DEFAULT_VALUE,
  onChange,
  disabled = false,
  onChangeTitle,
  titleTest,
  withLabel = true,
  defaultCourseId,
  disciplineId,
}: Readonly<AIQuizSectionProps>): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  const currentValue = { ...DEFAULT_VALUE, ...value };

  const handleTopicChange = useCallback(
    (topic: AIQuizTopic): void => {
      onChange({ ...currentValue, topic });
    },
    [currentValue, onChange]
  );

  const handleDifficultyChange = useCallback(
    (difficultyLevel: DifficultyLevel): void => {
      onChange({ ...currentValue, difficultyLevel });
    },
    [currentValue, onChange]
  );

  const handleNumberOfQuestionsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const rawValue = parseInt(e.target.value, 10);
      const numberOfQuestions = isNaN(rawValue) ? 1 : Math.min(Math.max(rawValue, 1), MAX_QUESTIONS);
      onChange({ ...currentValue, numberOfQuestions });
    },
    [currentValue, onChange]
  );

  const handleActivityTypesChange = useCallback(
    (activityTypeGuids: string[]): void => {
      onChange({ ...currentValue, activityTypeGuids });
    },
    [currentValue, onChange]
  );

  const handleEvaluationCriteriaChange = useCallback(
    (evaluationCriteria: EvaluationCriteria): void => {
      onChange({ ...currentValue, evaluationCriteria });
    },
    [currentValue, onChange]
  );

  const handleSpecialNeedsChange = useCallback(
    (specialNeeds: string[]): void => {
      onChange({ ...currentValue, specialNeeds });
    },
    [currentValue, onChange]
  );

  const handleAdditionalNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      onChange({ ...currentValue, additionalNotes: e.target.value });
    },
    [currentValue, onChange]
  );

  return (
    <div className="ai-quiz-section">
      {withLabel && (
        <div className="ai-quiz-section__label">
          <span className="ai-quiz-section__badge">{t('chatbot:Tutor IA')}</span>
        </div>
      )}

      <div className="ai-quiz-section__content">
        {onChangeTitle && <p className="ai-quiz-section__description">{t('chatbot:Fill out the form to get a personalized quiz tailored to your students')}</p>}
        <div className="ai-quiz-section__fields">
          {onChangeTitle && (
            <div className="ai-quiz-section__field">
              <FormField isRequired label={t('chatbot:Title')}>
                <InputText placeholder={t('chatbot:Write a title')} onChange={onChangeTitle} value={titleTest} />
              </FormField>
            </div>
          )}

          <div className="ai-quiz-section__field">
            <TextInputOrLessonSelector
              value={currentValue.topic}
              onChange={handleTopicChange}
              label={t('chatbot:What topic should the quiz cover')}
              disabled={disabled}
              showLabel={true}
              required={true}
              defaultCourseId={defaultCourseId}
            />
          </div>

          <div className="ai-quiz-section__field">
            <EducationYearSelector
              required={true}
              value={currentValue.difficultyLevel}
              onChange={handleDifficultyChange}
              disabled={disabled}
              showLabel={true}
            />
          </div>

          <div className="ai-quiz-section__row">
            <div className="ai-quiz-section__field ai-quiz-section__field--half">
              <FormField
                helperText={t('chatbot:Add max X questions', { count: MAX_QUESTIONS })}
                isDisabled={disabled}
                isHelperTextAboveInput
                isRequired
                label={t('chatbot:Number of questions')}
              >
                <InputText
                  placeholder=''
                  type="number"
                  value={String(currentValue.numberOfQuestions)}
                  onChange={handleNumberOfQuestionsChange}
                  min={1}
                  max={MAX_QUESTIONS}
                />
              </FormField>
            </div>

            <div className="ai-quiz-section__field ai-quiz-section__field--half">
              <ActivityTypeSelector required={true} value={currentValue.activityTypeGuids} onChange={handleActivityTypesChange} disabled={disabled} />
            </div>
          </div>

          <div className="ai-quiz-section__field">
            <EvaluationCriteriaSelector
              disciplineId={disciplineId}
              educationLevel={currentValue.difficultyLevel?.educationLevel}
              educationYear={currentValue.difficultyLevel?.educationYear}
              onChange={handleEvaluationCriteriaChange}
              value={currentValue.evaluationCriteria}
            />
          </div>

          <div className="ai-quiz-section__field">
            <SpecialNeedsSelector value={currentValue.specialNeeds} onChange={handleSpecialNeedsChange} disabled={disabled} />
          </div>

          <div className="ai-quiz-section__field">
            <Textarea
              label={t('chatbot:Additional notes')}
              placeholder={t('chatbot:Additional notes placeholder')}
              value={currentValue.additionalNotes}
              onChange={handleAdditionalNotesChange}
              disabled={disabled}
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIQuizSection;
